// client/src/pages/School_Admin/StudentManagement/TranscriptPage.js
// Generate a cumulative student transcript across selected classes/years (sec phase).
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Autocomplete, TextField, Button,
  List, ListItem, ListItemText, Checkbox, CircularProgress, Snackbar, Alert, Divider,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import { fetchJson, resolveApiUrl } from '../../../utils/fetchJson';

function getToken() {
  const keys = ['accessToken', 'token', 'authToken', 'jwt', 'id_token'];
  for (const k of keys) {
    const v = localStorage.getItem(k) || sessionStorage.getItem(k);
    if (v) return v;
  }
  return null;
}

export default function TranscriptPage() {
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [options, setOptions] = useState([]);
  const [checked, setChecked] = useState({}); // key `${academicYearId}:${classId}` -> bool
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reportLanguage, setReportLanguage] = useState('en');
  const [letterheadTemplate, setLetterheadTemplate] = useState('default');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  // Default language + letterhead to the school's saved settings.
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetchJson('/school-setup-and-configuration/school-settings', { method: 'GET' });
        const school = (resp && (resp.data?.school || resp.school || resp.data || resp)) || {};
        const lang = String(school.reportCardLanguage || school.customFields?.reportCardLanguage || 'en').toLowerCase() === 'fr' ? 'fr' : 'en';
        setReportLanguage(lang);
        const lh = school.reportCardLetterhead || school.customFields?.reportCardLetterhead;
        if (['default', 'ministry_school_en', 'ministry_school_fr'].includes(lh)) setLetterheadTemplate(lh);
      } catch (_) {}
    })();
  }, []);

  // Load classes for the secondary phase (transcripts are sec-phase only).
  useEffect(() => {
    (async () => {
      try {
        const phase = localStorage.getItem('schoolAdmin_activePhase') || 'sec';
        const data = await fetchJson(`/api/classes-lite?phase=${encodeURIComponent(phase)}`);
        const list = Array.isArray(data?.classes) ? data.classes : (Array.isArray(data) ? data : (data?.data || []));
        setClasses(list.map(c => ({ id: c.id, label: c.name || `Class ${c.id}` })));
      } catch (e) {
        setSnack({ open: true, message: t('transcript.errors.loadClasses') || 'Failed to load classes', severity: 'error' });
      }
    })();
  }, [t]);

  // When a class is picked, load its students into the student selector.
  useEffect(() => {
    setStudents([]);
    setSelectedStudent(null);
    if (!selectedClass?.id) return;
    (async () => {
      setLoadingStudents(true);
      try {
        const data = await fetchJson(`/api/report-cards/students?classId=${encodeURIComponent(selectedClass.id)}`);
        const list = data?.students || data?.data || (Array.isArray(data) ? data : []);
        const seen = new Set();
        const opts = [];
        for (const s of list) {
          if (seen.has(s.id)) continue;
          seen.add(s.id);
          const name = s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim();
          opts.push({ id: s.id, label: `${name}${s.studentNumber ? ` (${s.studentNumber})` : ''}` });
        }
        setStudents(opts);
      } catch (e) {
        setSnack({ open: true, message: t('transcript.errors.loadStudents') || 'Failed to load students', severity: 'error' });
      } finally {
        setLoadingStudents(false);
      }
    })();
  }, [selectedClass, t]);

  const loadOptions = useCallback(async (studentId) => {
    setOptions([]);
    setChecked({});
    if (!studentId) return;
    setLoadingOptions(true);
    try {
      const resp = await fetchJson(`/api/report-cards/transcript/options/${studentId}`);
      const opts = resp?.data || [];
      setOptions(opts);
      const init = {};
      opts.forEach(o => { init[`${o.academicYearId}:${o.classId}`] = true; });
      setChecked(init);
    } catch (e) {
      setSnack({ open: true, message: t('transcript.errors.loadOptions') || 'Failed to load enrollment history', severity: 'error' });
    } finally {
      setLoadingOptions(false);
    }
  }, [t]);

  useEffect(() => { loadOptions(selectedStudent?.id); }, [selectedStudent, loadOptions]);

  const toggle = (key) => setChecked(prev => ({ ...prev, [key]: !prev[key] }));

  const generate = async () => {
    if (!selectedStudent) return;
    const selections = options
      .filter(o => checked[`${o.academicYearId}:${o.classId}`])
      .map(o => ({ academicYearId: o.academicYearId, classId: o.classId }));
    if (selections.length === 0) {
      setSnack({ open: true, message: t('transcript.errors.selectAtLeastOne') || 'Select at least one class/year', severity: 'warning' });
      return;
    }
    setGenerating(true);
    try {
      const token = getToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: token.toLowerCase().startsWith('bearer ') ? token : `Bearer ${token}` } : {})
      };
      const storedPhase = localStorage.getItem('schoolAdmin_activePhase');
      if (storedPhase) headers['X-Active-Phase'] = storedPhase;
      const storedPhaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId');
      if (storedPhaseId) headers['X-Active-Phase-Id'] = String(storedPhaseId);

      const response = await fetch(resolveApiUrl('/api/report-cards/generate/transcript'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ studentId: selectedStudent.id, selections, reportLanguage, letterheadTemplate }),
        credentials: 'include'
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.error || err?.message || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setSnack({ open: true, message: t('transcript.success') || 'Transcript generated', severity: 'success' });
    } catch (e) {
      setSnack({ open: true, message: e?.message || (t('transcript.errors.generate') || 'Failed to generate transcript'), severity: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom>{t('transcript.title') || 'Student Transcript'}</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('transcript.subtitle') || 'Generate a cumulative transcript across the classes and academic years a student has completed.'}
      </Typography>

      <Card sx={{ maxWidth: 720 }}>
        <CardContent>
          <Autocomplete
            options={classes}
            value={selectedClass}
            onChange={(e, v) => setSelectedClass(v)}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => <TextField {...params} label={t('transcript.selectClass') || 'Select class'} />}
            sx={{ mb: 2 }}
          />
          <Autocomplete
            options={students}
            value={selectedStudent}
            onChange={(e, v) => setSelectedStudent(v)}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            disabled={!selectedClass || loadingStudents}
            loading={loadingStudents}
            renderInput={(params) => <TextField {...params} label={t('transcript.selectStudent') || 'Select student'} />}
          />

          {loadingOptions && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}><CircularProgress size={28} /></Box>
          )}

          {!loadingOptions && selectedStudent && options.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>{t('transcript.noHistory') || 'No enrollment history found for this student.'}</Alert>
          )}

          {!loadingOptions && options.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('transcript.selectPeriods') || 'Select the classes / years to include:'}
              </Typography>
              <Divider />
              <List dense>
                {options.map((o) => {
                  const key = `${o.academicYearId}:${o.classId}`;
                  return (
                    <ListItem key={key} disableGutters secondaryAction={
                      <Checkbox edge="end" checked={!!checked[key]} onChange={() => toggle(key)} />
                    }>
                      <ListItemText primary={o.className} secondary={o.academicYearName} />
                    </ListItem>
                  );
                })}
              </List>
              <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="tr-lang">{t('transcript.language') || 'Language'}</InputLabel>
                  <Select labelId="tr-lang" label={t('transcript.language') || 'Language'} value={reportLanguage} onChange={(e) => setReportLanguage(e.target.value)}>
                    <MenuItem value="en">{t('transcript.languageEn') || 'English'}</MenuItem>
                    <MenuItem value="fr">{t('transcript.languageFr') || 'French'}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="tr-lh">{t('transcript.letterhead') || 'Letterhead'}</InputLabel>
                  <Select labelId="tr-lh" label={t('transcript.letterhead') || 'Letterhead'} value={letterheadTemplate} onChange={(e) => setLetterheadTemplate(e.target.value)}>
                    <MenuItem value="default">{t('transcript.letterheadDefault') || 'Default (bilingual ministry)'}</MenuItem>
                    <MenuItem value="ministry_school_en">{t('transcript.letterheadEn') || 'Ministry + School info (English)'}</MenuItem>
                    <MenuItem value="ministry_school_fr">{t('transcript.letterheadFr') || 'Ministry + School info (French)'}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Button
                variant="contained"
                startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <DescriptionIcon />}
                onClick={generate}
                disabled={generating}
                sx={{ mt: 2 }}
              >
                {generating ? (t('transcript.generating') || 'Generating…') : (t('transcript.generate') || 'Generate Transcript')}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} sx={{ width: '100%' }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
